import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Eye, ShoppingCart, Printer, Wallet, Pencil, Plus, Send } from 'lucide-react';
import { customersApi, ordersApi, sellersApi } from '../api';
import {
  canShareToTelegram, openReceiptPrint, receiptError, shareReceiptToTelegram,
} from '../app/receipt';
import { formatPrice } from '../store';
import { Modal } from '../components/ui/Modal';
import {
  STATUS_LABEL, renderOrderStatus, isOverdue,
} from '../components/ui/orderStatus';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { PaymentDialog, PaymentMethod } from '../components/ui/PaymentDialog';
import { Pagination, DEFAULT_PAGE_SIZE } from '../components/ui/Pagination';
import type { OrderListItem, Order, Customer, Seller } from '../types';
import { useAuthStore } from '../store/auth';
import { matches } from '../lib/translit';

// Holat emas, "to'lanmagan qoldig'i bor" degan filtr uchun maxsus qiymat.
const HAS_DEBT = 'has_debt';

export const Orders: React.FC = () => {
  const toast = useToast();
  const canEditOrders = useAuthStore((s) => s.permissions.can_edit_orders);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [viewOrder, setViewOrder] = useState<Order | null>(null);
  const [editing, setEditing] = useState(false);
  const [editDue, setEditDue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editCustomer, setEditCustomer] = useState<number | ''>('');
  const [editSeller, setEditSeller] = useState<number | ''>('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Telegram'da chekni chatga yuborish mumkin — brauzerda bunday yo'l yo'q.
  const canShare = canShareToTelegram();
  const [payingOpen, setPayingOpen] = useState(false);
  const [payingSaving, setPayingSaving] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      // "Qarzi bor" alohida: qarz ham `debt`, ham `partial` holatda bo'ladi,
      // shuning uchun status emas, to'lanmagan qoldiq bo'yicha filtrlanadi.
      if (statusFilter === HAS_DEBT) params.has_debt = 1;
      else if (statusFilter) params.status = statusFilter;

      const res = await ordersApi.list(params);
      const rawData = res.data as any;
      const data = Array.isArray(rawData) ? rawData : (rawData.results || []);
      const count = Array.isArray(rawData) ? rawData.length : (rawData.count || 0);
      setOrders(data);
      setTotal(count);
    } catch (error: any) {
      if (error.response?.status === 404 && page > 1) {
        setPage(page - 1);
        return;
      }
      console.error('Orders fetch error:', error);
      toast.error('Buyurtmalarni yuklashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter]);

  const searchMounted = React.useRef(false);
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchOrders();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const viewOrderDetails = async (id: number) => {
    try {
      const res = await ordersApi.get(id);
      setViewOrder(res.data);
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('Buyurtmani yuklashda xato');
    }
  };

  const openEdit = () => {
    if (!viewOrder) return;
    setEditDue(viewOrder.due_date || '');
    setEditNotes(viewOrder.notes || '');
    setEditCustomer(viewOrder.customer ?? '');
    setEditSeller(viewOrder.seller ?? '');
    setCustomerSearch('');
    setEditing(true);

    // Ro'yxatlar faqat tahrirlash ochilganda kerak.
    if (customers.length === 0) {
      customersApi.list({ page_size: 200 })
        .then((res) => {
          const raw = res.data as any;
          setCustomers(Array.isArray(raw) ? raw : raw.results || []);
        })
        .catch(() => setCustomers([]));
    }
    if (sellers.length === 0) {
      sellersApi.list()
        .then((res) => {
          const raw = res.data as any;
          setSellers(Array.isArray(raw) ? raw : raw.results || []);
        })
        .catch(() => setSellers([]));
    }
  };

  const saveEdit = async () => {
    if (!viewOrder) return;
    setSavingEdit(true);
    try {
      const res = await ordersApi.edit(viewOrder.id, {
        due_date: editDue || null,
        notes: editNotes,
        customer: editCustomer === '' ? null : Number(editCustomer),
        seller: editSeller === '' ? null : Number(editSeller),
      });
      setViewOrder(res.data);
      setEditing(false);
      toast.success('Buyurtma yangilandi');
      fetchOrders();
    } catch (error: any) {
      const data = error.response?.data;
      const msg = data?.due_date?.[0] || data?.due_date
        || data?.customer?.[0] || data?.customer
        || data?.seller?.[0] || data?.seller
        || data?.detail || 'Saqlashda xato';
      toast.error(typeof msg === 'string' ? msg : 'Saqlashda xato');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddPayment = async (amount: number, method: PaymentMethod) => {
    if (!viewOrder) return;
    setPayingSaving(true);
    try {
      const res = await ordersApi.addPayment(viewOrder.id, { amount, method });
      setViewOrder(res.data);
      setPayingOpen(false);
      toast.success("To'lov qabul qilindi");
      fetchOrders();
    } catch (error: any) {
      const data = error.response?.data;
      const msg = data?.amount?.[0] || data?.order?.[0] || data?.detail || "To'lovni saqlashda xato";
      toast.error(msg);
    } finally {
      setPayingSaving(false);
    }
  };

  const printReceipt = async () => {
    if (!viewOrder || printing) return;
    setPrinting(true);
    try {
      await openReceiptPrint(viewOrder.id);
    } catch (error) {
      console.error('Receipt print error:', error);
      toast.error(receiptError(error, 'Chekni chop etishda xatolik yuz berdi'));
    } finally {
      setPrinting(false);
    }
  };

  const shareReceipt = async () => {
    if (!viewOrder || sharing) return;
    setSharing(true);
    try {
      await shareReceiptToTelegram(viewOrder.id, (sent) => {
        if (sent) toast.success('Chek yuborildi');
      });
    } catch (error) {
      toast.error(receiptError(error, 'Chekni ulashib bo\'lmadi'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Buyurtmalar</h1>
        <Link to="/orders/new" className="btn btn-primary flex items-center gap-2 shrink-0">
          <Plus size={18} />
          <span className="hidden sm:inline">Yangi sotuv</span>
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Qidirish (raqam, mijoz)..."
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input md:w-48"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Barcha holatlar</option>
            <option value={HAS_DEBT}>Qarzi bor</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Orders list */}
      <div className="card overflow-hidden !p-0">
        {loading ? (
          <SkeletonRows />
        ) : orders.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="Buyurtma topilmadi" />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Raqam</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sana</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Mijoz</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Holat</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tovarlar</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Jami</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">To'langan</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qarz</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Muddat</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm font-medium">{order.number}</td>
                      <td className="py-3 px-4 text-gray-500 text-sm">
                        {new Date(order.created_at).toLocaleDateString('uz-UZ')}
                      </td>
                      <td className="py-3 px-4">{order.customer_name || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        {renderOrderStatus(order.status, order.status_display)}
                      </td>
                      <td className="py-3 px-4 text-center">{order.items_count}</td>
                      <td className="py-3 px-4 text-right font-medium whitespace-nowrap">
                        {formatPrice(Number(order.total))} UZS
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        {formatPrice(Number(order.paid_amount))} UZS
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        {Number(order.debt_amount) > 0 ? (
                          <span className="font-semibold text-danger-600">
                            {formatPrice(Number(order.debt_amount))} UZS
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-sm hidden lg:table-cell">
                        {order.due_date ? (
                          <span className={
                            isOverdue(order.due_date, Number(order.paid_amount), Number(order.total))
                              ? 'text-danger-600 font-medium'
                              : 'text-gray-600'
                          }>
                            {new Date(order.due_date).toLocaleDateString('uz-UZ')}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center">
                          <button
                            onClick={() => viewOrderDetails(order.id)}
                            className="p-2 text-primary-800 hover:bg-primary-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-gray-100">
              {orders.map((order) => {
                const overdue = order.due_date
                  ? isOverdue(order.due_date, Number(order.paid_amount), Number(order.total))
                  : false;
                return (
                  <button
                    key={order.id}
                    onClick={() => viewOrderDetails(order.id)}
                    className="w-full text-left p-4 active:bg-gray-50 flex flex-col gap-1.5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-semibold text-gray-900">#{order.number}</span>
                      {renderOrderStatus(order.status, order.status_display)}
                    </div>
                    {/* Chapdagi matn qisqaradi, o'ngdagi summa esa butun qoladi —
                        tor ekranda raqam o'rtasidan bo'linib ketmasin. */}
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-gray-600 truncate min-w-0">
                        {order.customer_name || "Noma'lum mijoz"}
                      </span>
                      {Number(order.debt_amount) > 0 && (
                        <span className="font-semibold text-danger-600 whitespace-nowrap shrink-0">
                          Qarz: {formatPrice(Number(order.debt_amount))} UZS
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="text-gray-400 truncate min-w-0">
                        {new Date(order.created_at).toLocaleDateString('uz-UZ')} • {order.items_count} ta tovar
                      </span>
                      <span className="font-semibold text-gray-900 whitespace-nowrap shrink-0">
                        {formatPrice(Number(order.total))} UZS
                      </span>
                    </div>
                    {order.due_date && (
                      <div className={`text-xs ${overdue ? 'text-danger-600 font-medium' : 'text-gray-400'}`}>
                        Qarz muddati: {new Date(order.due_date).toLocaleDateString('uz-UZ')}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            count={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </div>

      {/* View Order Modal */}
      <Modal
        open={!!viewOrder}
        onClose={() => setViewOrder(null)}
        title={viewOrder ? `Buyurtma #${viewOrder.number}` : ''}
        size="lg"
        footer={
          viewOrder ? (
            <>
              <button
                onClick={() => { setViewOrder(null); setEditing(false); }}
                className="btn btn-secondary"
              >
                Yopish
              </button>
              {editing ? (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="btn btn-secondary"
                  >
                    Bekor qilish
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={savingEdit}
                    className="btn btn-primary"
                  >
                    {savingEdit ? 'Saqlanmoqda...' : 'Saqlash'}
                  </button>
                </>
              ) : (
                <>
                  {canEditOrders && viewOrder.status !== 'cancelled' && (
                    <button onClick={openEdit} className="btn btn-secondary flex items-center gap-2">
                      <Pencil size={18} />
                      Tahrirlash
                    </button>
                  )}
                  {Number(viewOrder.debt_amount) > 0 && (
                    <button
                      onClick={() => setPayingOpen(true)}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <Wallet size={18} />
                      To'lov qabul qilish
                    </button>
                  )}
                  {canShare && (
                    <button
                      onClick={shareReceipt}
                      disabled={sharing}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <Send size={18} />
                      {sharing ? 'Tayyorlanmoqda...' : 'Telegram'}
                    </button>
                  )}
                  <button onClick={printReceipt} disabled={printing} className="btn btn-primary flex items-center gap-2">
                    <Printer size={18} />
                    {printing ? 'Tayyorlanmoqda...' : 'Chop etish'}
                  </button>
                </>
              )}
            </>
          ) : undefined
        }
      >
        {viewOrder && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Sana:</span>
                <span className="ml-2 font-medium">
                  {new Date(viewOrder.created_at).toLocaleString('uz-UZ')}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Holat:</span>
                <span className="ml-2">{renderOrderStatus(viewOrder.status, viewOrder.status_display)}</span>
              </div>
              <div>
                <span className="text-gray-500">Mijoz:</span>
                <span className="ml-2 font-medium">{viewOrder.customer_name || 'Nomaʼlum'}</span>
              </div>
              <div>
                <span className="text-gray-500">Sotuvchi:</span>
                <span className="ml-2 font-medium">{viewOrder.seller_name || '—'}</span>
              </div>
              {viewOrder.due_date && !editing && (
                <div>
                  <span className="text-gray-500">Qarz muddati:</span>
                  <span className={`ml-2 font-medium ${
                    isOverdue(viewOrder.due_date, Number(viewOrder.paid_amount), Number(viewOrder.total))
                      ? 'text-danger-600'
                      : ''
                  }`}>
                    {new Date(viewOrder.due_date).toLocaleDateString('uz-UZ')}
                  </span>
                </div>
              )}
              {viewOrder.notes && !editing && (
                <div className="col-span-2">
                  <span className="text-gray-500">Izoh:</span>
                  <span className="ml-2">{viewOrder.notes}</span>
                </div>
              )}
            </div>

            {/* Tahrirlash — faqat muddat va izoh. Summa, tovarlar va to'lovlar
                ombor va kassa bilan bog'liq, ular bu yerdan o'zgarmaydi. */}
            {editing && (
              <div className="rounded-lg border border-primary-100 bg-primary-50/40 p-4 space-y-3">
                <p className="text-sm text-gray-600">
                  Faqat qarz muddati va izoh o'zgaradi. Summa, tovarlar va
                  to'lovlarni tahrirlab bo'lmaydi — noto'g'ri buyurtmani bekor
                  qilib, qaytadan yozish kerak.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Mijoz</label>
                    {customers.length > 8 && (
                      <input
                        type="text"
                        className="input mb-2"
                        placeholder="Mijozni qidirish..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                      />
                    )}
                    <select
                      className="input"
                      value={editCustomer}
                      onChange={(e) => setEditCustomer(e.target.value ? Number(e.target.value) : '')}
                    >
                      <option value="">Mijozsiz</option>
                      {customers
                        .filter((c) => {
                          const q = customerSearch.trim();
                          if (!q) return true;
                          // Tanlangan mijoz filtrdan tushib qolmasin.
                          if (c.id === editCustomer) return true;
                          return matches(c.name, q) || (c.phone || '').includes(q);
                        })
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.phone ? ` — ${c.phone}` : ''}
                          </option>
                        ))}
                    </select>
                    {Number(viewOrder.debt_amount) > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Mijoz almashtirilsa, bu buyurtmaning qarzi
                        ({formatPrice(Number(viewOrder.debt_amount))} UZS) va unga
                        yozilgan to'lovlar ham yangi mijozga ko'chadi.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label">Sotuvchi</label>
                    <select
                      className="input"
                      value={editSeller}
                      onChange={(e) => setEditSeller(e.target.value ? Number(e.target.value) : '')}
                    >
                      <option value="">Belgilanmagan</option>
                      {sellers.map((seller) => (
                        <option key={seller.id} value={seller.id}>{seller.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Qarz muddati</label>
                    <input
                      type="date"
                      className="input"
                      value={editDue}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setEditDue(e.target.value)}
                    />
                    {editDue && (
                      <button
                        type="button"
                        className="text-sm text-gray-500 hover:underline mt-1"
                        onClick={() => setEditDue('')}
                      >
                        Muddatni olib tashlash
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="label">Izoh</label>
                    <textarea
                      className="input"
                      rows={2}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3">Tovar</th>
                    <th className="text-right py-2 px-3">Narxi</th>
                    <th className="text-center py-2 px-3">Soni</th>
                    <th className="text-right py-2 px-3">Jami</th>
                  </tr>
                </thead>
                <tbody>
                  {viewOrder.items.map((item) => {
                    const original = item.original_unit_price ? Number(item.original_unit_price) : null;
                    const applied = Number(item.unit_price);
                    const discounted = original !== null && applied < original;
                    return (
                      <tr key={item.id} className="border-t border-gray-100">
                        <td className="py-2 px-3">
                          <div>{item.product_name}</div>
                          <div className="text-gray-400 text-xs">{item.product_sku}</div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          {discounted ? (
                            <div>
                              <div className="text-gray-400 line-through text-xs">{formatPrice(original!)}</div>
                              <div className="text-danger-600 font-medium">{formatPrice(applied)}</div>
                            </div>
                          ) : (
                            formatPrice(applied)
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">{item.quantity}</td>
                        <td className="py-2 px-3 text-right font-medium">{formatPrice(Number(item.total))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <div className="w-full sm:w-64 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Jami:</span>
                  <span>{formatPrice(Number(viewOrder.subtotal))} UZS</span>
                </div>
                {Number(viewOrder.discount_amount) > 0 && (
                  <div className="flex justify-between text-success-600">
                    <span>Chegirma ({viewOrder.discount_percent}%):</span>
                    <span>-{formatPrice(Number(viewOrder.discount_amount))} UZS</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base pt-1 border-t border-gray-100">
                  <span>Yakuniy:</span>
                  <span>{formatPrice(Number(viewOrder.total))} UZS</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">To'langan:</span>
                  <span className="text-success-600">{formatPrice(Number(viewOrder.paid_amount))} UZS</span>
                </div>
                {Number(viewOrder.debt_amount) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Shu buyurtma qarzi:</span>
                    <span className="text-danger-600">{formatPrice(Number(viewOrder.debt_amount))} UZS</span>
                  </div>
                )}

                {/* Bitim manzarasi: sotuv paytida mijozning eski qarzi ham
                    bo'lgan bo'lsa, chekdagi raqamlar shu yerda takrorlanadi. */}
                {Number(viewOrder.customer_previous_debt) > 0 && (
                  <div className="pt-2 mt-2 border-t border-gray-100 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sotuv paytidagi eski qarz:</span>
                      <span className="text-danger-600">
                        {formatPrice(Number(viewOrder.customer_previous_debt))} UZS
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bitim bo'yicha jami:</span>
                      <span>{formatPrice(Number(viewOrder.grand_total))} UZS</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Olingan pul:</span>
                      <span className="text-success-600">
                        {formatPrice(Number(viewOrder.received_total))} UZS
                      </span>
                    </div>
                  </div>
                )}

                {viewOrder.customer_debt !== null && (
                  <div className="flex justify-between pt-2 mt-2 border-t border-gray-100 font-semibold">
                    <span className="text-gray-500">Mijozning umumiy qarzi:</span>
                    <span className={Number(viewOrder.customer_debt) > 0 ? 'text-danger-600' : 'text-success-600'}>
                      {formatPrice(Number(viewOrder.customer_debt))} UZS
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* To'lov qabul qilish */}
      {viewOrder && (
        <PaymentDialog
          open={payingOpen}
          title={`#${viewOrder.number} — to'lov`}
          hint="Shu buyurtmadagi qarz"
          maxAmount={Number(viewOrder.debt_amount)}
          saving={payingSaving}
          onClose={() => setPayingOpen(false)}
          onSubmit={handleAddPayment}
        />
      )}
    </div>
  );
};
